import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { update } from 'lodash/fp';

import {
  getProviderApproversAction,
  updateProviderApprovalTemplateAction,
  getProviderApprovalTemplateAction,
  createProviderApprovalTemplateAction,
  deleteProviderApprovalTemplateAction,
} from 'domain/paySettings';

import useDispatchAction from 'hooks/useDispatchAction';

import LevelItem from './components/LevelItem';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import LinearProgress from '@mui/material/LinearProgress';

import { Fields } from 'pages/configurations/company/pages/approvals/pages/advancedGroups/helpers';
import {
  ErrorsFactory,
  formDataFactory,
  levelItemErrorFactory,
  levelItemFactory,
  levelItemTouchedFactory,
  TouchedFactory,
} from 'pages/configurations/company/pages/approvals/pages/advancedGroups/components/AdvancedGroupDialog/helpers';
import { addUsersDataToTemplate } from './helpers';

import elements from 'components/elements';

interface ApprovalFlowProps {
  providerId: string;
}

const ApprovalFlow: React.FC<ApprovalFlowProps> = ({ providerId }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const [values, setValues] = useState(formDataFactory({}));
  const [approvalTemplateId, setApprovalTemplateId] = useState<string | null>(null);
  const [errors, setErrors] = useState(ErrorsFactory());
  const [touched, setTouched] = useState(TouchedFactory());
  const [isSubmitting, setISSubmitting] = useState(false);

  const payload = useMemo(() => ({ id: providerId }), [providerId]);

  const { data: users, isLoading: isLoadingUsers } = useDispatchAction(getProviderApproversAction, payload, []);

  const { data: approvalFlowTemplateData, isLoading: isLoadingApprovalFlowTemplateData } = useDispatchAction(
    getProviderApprovalTemplateAction,
    payload,
    {},
  );

  useEffect(() => {
    if (!isLoadingUsers && !isLoadingApprovalFlowTemplateData) {
      const data = formDataFactory(addUsersDataToTemplate(approvalFlowTemplateData, users));
      setValues(formDataFactory(data));
      setTouched(TouchedFactory(data));
      setApprovalTemplateId(approvalFlowTemplateData.id);
    }
  }, [approvalFlowTemplateData, isLoadingUsers, isLoadingApprovalFlowTemplateData, users]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = values[Fields.levels].findIndex((level: any) => level.id === active.id);
        const newIndex = values[Fields.levels].findIndex((level: any) => level.id === over.id);
        setValues({
          ...values,
          levels: arrayMove(values[Fields.levels], oldIndex, newIndex),
        });
      }
    },
    [values],
  );

  const hasErrors = Object.values(errors)
    .flatMap((value) => (Array.isArray(value) ? value.flatMap(Object.values) : value))
    .some(Boolean);

  const enableSubmit = !hasErrors && !isSubmitting;
  const enableRemove = approvalTemplateId && !isSubmitting;

  const handleLevelChange = useCallback(
    (index: number, field: string, value: unknown) => {
      const updatedLevels = [...values[Fields.levels]];
      updatedLevels[index][field] = value;
      setValues({ ...values, [Fields.levels]: updatedLevels });
    },
    [values],
  );

  const addLevel = useCallback(() => {
    setValues({
      ...values,
      levels: [...values.levels, levelItemFactory()],
    });
    setTouched({
      ...touched,
      levels: [...touched.levels, levelItemTouchedFactory()],
    });
  }, [values, touched]);

  const removeLevel = useCallback(
    (index: number) => {
      const updatedLevels = values.levels.filter((_: any, i: number) => i !== index);
      const updatedTouchedLevels = touched.levels.filter((_: any, i: number) => i !== index);
      setValues({ ...values, levels: updatedLevels });
      setTouched({ ...errors, levels: updatedTouchedLevels });
    },
    [values, touched, errors],
  );

  const onSubmit = useCallback(
    async (values: any) =>
      new Promise((resolve, reject) => {
        const payload = {
          resolve,
          reject,
          ...update(
            [Fields.levels],
            (levels: any[]) =>
              levels.map((level: any) => {
                const { [Fields.minAmount]: minAmount, id, ...rest } = level;

                return {
                  ...rest,
                  [Fields.approvers]: level[Fields.approvers].map((approver: any) => approver.id),
                  minAmount: Number(minAmount),
                };
              }),
            values,
          ),
          id: providerId,
        };

        const action = approvalTemplateId ? updateProviderApprovalTemplateAction : createProviderApprovalTemplateAction;
        dispatch(action(payload));
      })
        .then((data: any) => {
          setApprovalTemplateId(data.id);
        })
        .catch(() => {}),
    [dispatch, providerId, approvalTemplateId],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      try {
        setISSubmitting(true);
        await onSubmit(values);
        setTouched(TouchedFactory(values));
      } finally {
        setISSubmitting(false);
      }
    },
    [onSubmit, values],
  );

  const removeApprovalTemplate = useCallback(async () => {
    setISSubmitting(true);
    new Promise((resolve, reject) => {
      const payload = {
        resolve,
        reject,
        id: providerId,
      };
      dispatch(deleteProviderApprovalTemplateAction(payload));
    })
      .then(() => {
        setValues(formDataFactory());
        setApprovalTemplateId(null);
      })
      .catch(() => {})
      .finally(() => {
        setISSubmitting(false);
      });
  }, [dispatch, providerId]);

  useEffect(() => {
    const duplicateAmounts = values[Fields.levels]
      .map((level: any) => Number(level[Fields.minAmount]))
      .filter((amount: number, index: number, self: number[]) => self.indexOf(amount) !== index);

    setErrors(
      ErrorsFactory({
        [Fields.levels]: values[Fields.levels].map((level: any) => {
          const minAmount = level[Fields.minAmount];
          const approvers = level[Fields.approvers];

          let minAmountError = null;
          if (minAmount === null) {
            minAmountError = 'required';
          } else if (duplicateAmounts.includes(Number(minAmount))) {
            minAmountError = formatMessage({
              id: 'message.unique_amounts_per_level_required',
              defaultMessage: 'Amounts must be unique for each level',
            });
          }

          const approversError = approvers.length ? null : 'should have at least one approval!';

          return levelItemErrorFactory({
            [Fields.minAmount]: minAmountError,
            [Fields.approvers]: approversError,
          });
        }),
      }),
    );
  }, [values, formatMessage]);

  return (
    <>
      {isLoadingUsers || isLoadingApprovalFlowTemplateData ? (
        <LinearProgress />
      ) : (
        <Box p={3}>
          <form onSubmit={handleSubmit} noValidate>
            <Stack direction="row" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                color="error"
                disabled={!enableRemove}
                onClick={removeApprovalTemplate}
                data-element={elements.payModule.popup.paymentProviderDetails.approvalFlow.remove}
              >
                {formatMessage({
                  id: 'button.remove',
                  defaultMessage: 'Remove',
                })}
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isSubmitting}
                disabled={!enableSubmit}
                data-element={elements.payModule.popup.paymentProviderDetails.approvalFlow.save}
              >
                {formatMessage({
                  id: 'button.save',
                  defaultMessage: 'Save',
                })}
              </LoadingButton>
            </Stack>
            <Box>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={values[Fields.levels].map((level: any) => level.id)} strategy={rectSortingStrategy}>
                  {values[Fields.levels].map((level: any, i: number) => (
                    <LevelItem
                      key={level.id}
                      i={i}
                      levelData={level}
                      isSubmitting={isSubmitting}
                      setTouched={setTouched}
                      touched={touched}
                      errors={errors}
                      handleLevelChange={handleLevelChange}
                      removeLevel={removeLevel}
                      canBeDelete={values[Fields.levels].length !== 1}
                      users={users}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </Box>
            <Button
              variant="text"
              startIcon={<AddOutlinedIcon />}
              onClick={addLevel}
              data-element={elements.payModule.popup.paymentProviderDetails.approvalFlow.addLevelBtn}
            >
              {formatMessage({
                id: 'configurations.company.approvals.advancedGroups.form.addLevel',
                defaultMessage: 'Add level',
              })}
            </Button>
          </form>
        </Box>
      )}
    </>
  );
};

export default ApprovalFlow;
